import { Box, Snackbar, Alert, Typography } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import api from "../../services/api";
import HomeHeader from "./components/HomeHeader";
import SummaryStrip from "./components/SummaryStrip";
import ProductsSection from "./components/ProductsSection";
import MapsWidget from "./components/MapsWidget";
import ReportsWidget from "./components/ReportsWidget";
import { getModulos, getPerfil, buildAccess } from "./components/utils";

// quantidade de itens exibidos em cada card de "Atividade recente"
const ACTIVITY_LIMIT = 5;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  body: {
    gap: "1.5rem",
    display: "flex",
    flexDirection: "column",
    width: "100%",
  },
  activityGrid: {
    display: "grid",
    gridTemplateColumns: {
      xs: "1fr",
      lg: "repeat(auto-fit, minmax(340px, 1fr))",
    },
    gap: 2,
    width: "100%",
    alignItems: "start",
  },
};

export default function NocLinks() {
  const [recentMaps, setRecentMaps] = useState([]);
  const [mapsSummary, setMapsSummary] = useState(null);
  const [loadingMaps, setLoadingMaps] = useState(true);
  const [reports, setReports] = useState([]);
  const [reportsTotal, setReportsTotal] = useState(null);
  const [schedulesCount, setSchedulesCount] = useState(0);
  const [loadingReports, setLoadingReports] = useState(true);
  const [snackbar, setSnackbar] = useState(null);

  const perfil = useMemo(getPerfil, []);
  const modulos = useMemo(getModulos, []);
  const access = useMemo(() => buildAccess(modulos), [modulos]);

  const nome = perfil?.nome || perfil?.nomeFantasia || "";
  const foto = perfil?.foto || "";

  useEffect(() => {
    if (!access.easyMaps) {
      setLoadingMaps(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const [recentRes, summaryRes] = await Promise.allSettled([
          // alertas=false: a home não exibe alertas e o cálculo custa uma
          // chamada à API do Zabbix por mapa
          api.get("/easyMaps/recentMaps", {
            params: { limit: ACTIVITY_LIMIT, alertas: false },
          }),
          // totais de todos os mapas, independentes do limite acima
          api.get("/easyMaps/map/summary"),
        ]);
        if (!active) return;
        // slice defensivo: backend sem suporte ao limit devolveria mais itens
        if (
          recentRes.status === "fulfilled" &&
          Array.isArray(recentRes.value?.data)
        )
          setRecentMaps(recentRes.value.data.slice(0, ACTIVITY_LIMIT));
        if (summaryRes.status === "fulfilled" && summaryRes.value?.data)
          setMapsSummary(summaryRes.value.data);
      } catch {
        if (active) setRecentMaps([]);
      } finally {
        if (active) setLoadingMaps(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [access.easyMaps]);

  useEffect(() => {
    if (!access.easyReport) {
      setLoadingReports(false);
      return;
    }
    let active = true;
    (async () => {
      try {
        const [reportsRes, schedulesRes] = await Promise.allSettled([
          // hosts=false: o widget mostra título e data, não hosts
          api.get("/easyReport/v2/findManyReports", {
            params: { limit: ACTIVITY_LIMIT, hosts: false },
          }),
          // só o total interessa aqui
          api.get("/easyReport/v2/findManySchedules", {
            params: { onlyCount: true },
          }),
        ]);
        if (!active) return;
        if (reportsRes.status === "fulfilled") {
          // com limite a rota responde { items, total }; sem limite, um array
          const data = reportsRes.value?.data;
          const items = Array.isArray(data?.items)
            ? data.items
            : Array.isArray(data)
              ? data
              : [];
          setReports(items.slice(0, ACTIVITY_LIMIT));
          setReportsTotal(
            Number.isFinite(data?.total) ? data.total : items.length,
          );
        }
        if (schedulesRes.status === "fulfilled") {
          // com onlyCount a rota responde { total }; sem ele, a lista completa
          const data = schedulesRes.value?.data;
          setSchedulesCount(
            Number.isFinite(data?.total)
              ? data.total
              : Array.isArray(data)
                ? data.length
                : 0,
          );
        }
      } catch {
        if (active) setReports([]);
      } finally {
        if (active) setLoadingReports(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [access.easyReport]);

  const showActivity = access.easyMaps || access.easyReport;

  return (
    <Box sx={styles.container}>
      <Box sx={styles.body}>
        <HomeHeader nome={nome} foto={foto} suspended={access.suspended} />

        <SummaryStrip
          access={access}
          recentMaps={recentMaps}
          mapsSummary={mapsSummary}
          reports={reports}
          reportsTotal={reportsTotal}
          schedulesCount={schedulesCount}
          loading={loadingMaps || loadingReports}
        />

        <ProductsSection access={access} />

        {showActivity && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <Box>
              <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
                Atividade recente
              </Typography>
              <Typography sx={{ fontSize: "0.85rem", color: "text.secondary" }}>
                O que aconteceu por último nos seus módulos.
              </Typography>
            </Box>
            <Box sx={styles.activityGrid}>
              {access.easyMaps && (
                <MapsWidget recentMaps={recentMaps} loading={loadingMaps} />
              )}
              {access.easyReport && (
                <ReportsWidget reports={reports} loading={loadingReports} />
              )}
            </Box>
          </Box>
        )}
      </Box>

      {!!snackbar && (
        <Snackbar
          open
          onClose={() => setSnackbar(null)}
          autoHideDuration={2000}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        >
          <Alert {...snackbar} onClose={() => setSnackbar(null)} />
        </Snackbar>
      )}
    </Box>
  );
}
